import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Input } from 'src/@vymo/ui/atoms';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { ReactComponent as LocationIcon } from 'src/assets/icons/location.svg';
import { ReactComponent as SearchIcon } from 'src/assets/icons/search.svg';
import { trackEvent } from 'src/logEvent';
import styles from './index.module.scss';

interface Props {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  selectedPlace: google.maps.GeocoderResult | null;
}

export function PlaceAutocomplete({ onPlaceSelect, selectedPlace }: Props) {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');
  const map = useMap();
  const [locationText, setLocationText] = useState(
    selectedPlace?.formatted_address,
  );
  useEffect(() => {
    setLocationText(selectedPlace?.formatted_address);
  }, [selectedPlace?.formatted_address]);

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['geometry', 'formatted_address'],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (selectedPlace?.geometry?.viewport) {
      trackEvent('Location_Viewport_Fitted', {
        place: selectedPlace.formatted_address,
      });
      map?.fitBounds(selectedPlace.geometry?.viewport);
    }
  }, [selectedPlace, map]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace();

      onPlaceSelect(place);
      trackEvent('Location_Place_Selected', {
        place: place.formatted_address,
      });
    });
  }, [onPlaceSelect, placeAutocomplete]);

  const replacePacIcons = useCallback(() => {
    const pacIcons = document.querySelectorAll('.pac-icon');
    pacIcons.forEach((icon) => {
      const container = document.createElement('span');
      container.className = 'custom-pac-icon';
      icon.parentNode?.replaceChild(container, icon);
      const root = ReactDOM.createRoot(container);
      root.render(<LocationIcon />);
    });
  }, []);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (
                element.classList?.contains('pac-item') ||
                element.querySelector?.('.pac-item')
              ) {
                setTimeout(replacePacIcons, 10);
              }
            }
          });
        }
      });
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [replacePacIcons]);

  const onChangeLocationInput = useCallback((event) => {
    setLocationText(event?.target.value);
  }, []);

  return (
    <div className={styles.autocomplete_container}>
      <Input
        ref={inputRef}
        value={locationText}
        onChange={onChangeLocationInput}
        data-test-id="location_input"
        placeholder="Enter Location"
        iconLeft={<SearchIcon />}
      />
    </div>
  );
}
